'use client';

import React, { useCallback, useContext, useMemo, useRef, useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { themeQuartz } from '@ag-grid-community/theming';
import { iconSetAlpine } from 'ag-grid-community';
import { HotKeys } from './hot-keys';
import { TrashIcon, ListPlusIcon, NotePencilIcon, SortAscendingIcon, SortDescendingIcon, CaretDownIcon, MagicWandIcon, CheckIcon, DownloadSimpleIcon } from '@phosphor-icons/react';
import { BLANK_SLIDE } from '@/shared/constants';
import { Slide, View } from '@/shared/types';
import { Button } from '@heroui/button';
import { Spinner } from '@heroui/spinner';
import { sortByRealDatetime } from '@/shared/clientFunctions';
import { Tooltip } from '@heroui/tooltip';
import { Dropdown, DropdownTrigger, DropdownMenu, DropdownItem } from '@heroui/dropdown';
import { Popover, PopoverTrigger, PopoverContent } from '@heroui/popover';
// Table cell renderers
import Dates from './cells/dates';
import Visibility from './cells/visibility';
import Titles from './cells/titles';
import Descriptions from './cells/descriptions';
import { evidenceTypeValues } from './cells/evidenceTypes';
import Times from './cells/times';
import Media from './cells/media';
import SimplifiedSources from './cells/simplifiedSources';
import Actions from './cells/actions';
import DisplayOrder from './cells/displayOrder';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from '@heroui/modal';
import { getTimezoneListByName, recordStat, FetchWithVersionCheck } from '@/shared/functions';
import { StatsContext } from '@/app/contexts';
import { AuthContext } from '@/app/contexts';
import { Source } from '@/shared/types';
import EventsReviewModal from './events-review-modal';
dayjs.extend(utc);
dayjs.extend(timezone);
export const columnEditMappings: Record<string, {
  columnName: string;
  options: string[];
}> = {
  'evidenceType': {
    columnName: 'Evidence Type',
    options: evidenceTypeValues.filter((v): v is string => v !== undefined)
  },
  'slideDescription': {
    columnName: 'Description',
    options: []
  },
  'slideSubTitle': {
    columnName: 'Subtitle',
    options: []
  },
  'slideTitle': {
    columnName: 'Title',
    options: []
  },
  'media': {
    columnName: 'Media',
    options: []
  },
  'sources': {
    columnName: 'Sources',
    options: []
  },
  'actions': {
    columnName: '',
    options: []
  },
  'date': {
    columnName: 'Date',
    options: []
  },
  'time': {
    columnName: 'Time',
    options: []
  }
};
const myTheme = themeQuartz.withPart(iconSetAlpine).withParams({
  accentColor: "#FFFFFF54",
  backgroundColor: "#0F1D32",
  borderColor: "#FFFFFF12",
  borderRadius: 20,
  browserColorScheme: "dark",
  cellHorizontalPaddingScale: 0.5,
  checkboxBorderRadius: "3px",
  chromeBackgroundColor: {
    ref: "backgroundColor"
  },
  columnBorder: false,
  fontFamily: "inherit",
  fontSize: "14px",
  foregroundColor: "#FCFCFC",
  headerBackgroundColor: "#09121E",
  headerFontSize: 16,
  headerFontWeight: 500,
  headerTextColor: "#FFFFFF",
  headerVerticalPaddingScale: .5,
  headerColumnResizeHandleColor: "#FFFFFF12",
  headerColumnResizeHandleWidth: 3,
  headerColumnResizeHandleHeight: 20,
  iconSize: "16px",
  inputBorder: false,
  inputBorderRadius: "0px",
  inputFocusBackgroundColor: "#FFFFFF00",
  inputFocusBorder: false,
  inputFocusShadow: "#0000002A 0px 0px 0px 3px",
  inputFocusTextColor: "#FCFCFC",
  inputTextColor: "#FCFCFC",
  oddRowBackgroundColor: "#FFFFFF10",
  rowBorder: false,
  rowVerticalPaddingScale: .5,
  sidePanelBorder: true,
  spacing: 8,
  wrapperBorder: true,
  wrapperBorderRadius: "7px"
});

// Interfaces and types
interface TPTableProps {
  allSlides: Slide[];
  caseId: string;
  timelineId: string;
  title?: string;
  description?: string;
  media?: string;
  updateTimeline: (action: 'delete' | 'update' | 'order', rows: Slide[]) => void;
  onOpenModal: (newEvent: Slide, column?: string | null) => void;
  onSetEventIndex: (index: number) => void;
  onEditEvent: (updatedEvent: Slide) => void;
  isLegacy: boolean;
  isEditableTimeline: boolean;
  onAddEvent: (newEvent: Slide) => void;
  onDeleteEvent: (event: Slide) => void;
  onDeleteTimeline: () => void;
  sortByDateTime: boolean;
  enrichEvents: (parsedEvents: Slide[], useEnrichment: boolean, edit: boolean, timelineIdIn?: string) => Promise<void>;
  createEventsFromParsedEvents: (parsedEvents: Slide[], enriching: boolean, edit: boolean, timelineIdIn?: string) => Promise<void>;
  entityExtractionJobs: any[];
  getEventsFromEntityService: (action: string, timelineId: string | null, caseId: string, answerItemText: string, sources: Source[], epochTimeSecs: number, jobId: string | null, cancelPolling: boolean) => Promise<void>;
  createEventsFromEntityEvents: (jobId: string, events: any[], enriching: boolean, timelineIdIn?: string) => Promise<void>;
  featureFlags?: {
    [key: string]: any;
  };
  onViewChange?: (view: View) => void;
  onBackToWorkProducts?: () => void;
  selectedView?: View;
  onOpenTitleSlideModal: () => void;
}
interface StagedChange {
  slide: number;
  id: string;
  column: string;
  value: string;
}
interface EditingItems {
  editor: string;
  slide: number;
  columnDisplayName: string;
  column: string;
  items: Array<{
    id: string;
    value: any;
  }>;
}
const DateAdded = ({
  value
}: {
  value: string;
}) => {
  return <span data-sentry-component="DateAdded" data-sentry-source-file="tp-table.tsx">{dayjs(value).format('MM/DD/YYYY HH:mm:ss')}</span>;
};

// Component definition
const TPTable: React.FC<TPTableProps> = ({
  title,
  caseId,
  timelineId,
  description,
  media,
  allSlides,
  updateTimeline,
  onOpenModal,
  onSetEventIndex,
  onEditEvent,
  isLegacy,
  isEditableTimeline,
  onAddEvent,
  onDeleteEvent,
  onDeleteTimeline,
  sortByDateTime,
  enrichEvents,
  createEventsFromParsedEvents,
  entityExtractionJobs,
  getEventsFromEntityService,
  createEventsFromEntityEvents,
  featureFlags,
  onViewChange,
  onBackToWorkProducts,
  selectedView,
  onOpenTitleSlideModal
}) => {
  // State declarations
  const [editingItems, setEditingItems] = useState<EditingItems | null>(null);
  const [stagedChanges, setStagedChanges] = useState<Record<string, StagedChange>>({});
  const [deleteTimelineDialogOpen, setDeleteTimelineDialogOpen] = useState(false);
  const [deleteSelectedEventsDialogOpen, setDeleteSelectedEventsDialogOpen] = useState(false);
  const [enrichSelectedEventsDialogOpen, setEnrichSelectedEventsDialogOpen] = useState(false);
  const [selectedSortColumn, setSelectedSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | 'none'>('none');
  const [selectedRows, setSelectedRows] = useState<Slide[]>([]);
  const [currentEvent, setCurrentEvent] = useState<Slide | null>(null);
  const gridRef = useRef<AgGridReact>(null);
  const [applyOrderEnabled, setApplyOrderEnabled] = useState(false);
  const [extractionJobs, setExtractionJobs] = useState<any[]>([]);
  const [jobsBeingDeleted, setJobsBeingDeleted] = useState<Record<string, boolean>>({});
  const [jobsBeingApplied, setJobsBeingApplied] = useState<Record<string, boolean>>({});
  const [eventsReviewModalOpen, setEventsReviewModalOpen] = useState(false);
  const [eventsReviewModalQuestion, setEventsReviewModalQuestion] = useState('');
  const [eventsReviewModalExtractionJob, setEventsReviewModalExtractionJob] = useState<any>(null);
  const [loadingExtractedEvents, setLoadingExtractedEvents] = useState(false);
  const [extractedEventsPopoverOpen, setExtractedEventsPopoverOpen] = useState(false);
  const [exportingPptx, setExportingPptx] = useState(false);
  const [pptxJobId, setPptxJobId] = useState<string | null>(null);
  const [pptxJobPollCount, setPptxJobPollCount] = useState(0);
  const pptxJobPollRef = useRef<NodeJS.Timeout | null>(null);
  const statsContext = useContext(StatsContext);
  const {
    idToken,
    accessToken
  } = useContext(AuthContext);
  useEffect(() => {
    if (allSlides && allSlides.length > 0 && !currentEvent) {
      setCurrentEvent(allSlides[0]);
    }
  }, [allSlides, currentEvent]);
  useEffect(() => {
    setExtractionJobs(entityExtractionJobs || []);
    setTimeout(() => {
      setLoadingExtractedEvents(false);
    }, 1000);
  }, [entityExtractionJobs]);
  const handleCloseEventsReviewModal = async (open: boolean) => {
    if (open) {
      setEventsReviewModalOpen(true);
    } else {
      setEventsReviewModalOpen(false);
      setEventsReviewModalQuestion('');
      setEventsReviewModalExtractionJob(null);
      setLoadingExtractedEvents(true);
      setTimeout(async () => {
        await getEventsFromEntityService('GET_ALL_JOBS', '', caseId, '', [], 0, null, true);
      }, 1000);
    }
  };
  const handleVisibilityClick = useCallback((params: any) => {
    const index = params.data.displayOrder - 1;
    const event = allSlides[index];
    event.show = !event.show;
    if (!isLegacy) {
      onEditEvent(event);
    }
  }, [allSlides, isLegacy, onEditEvent]);
  const timezoneListByName = getTimezoneListByName();
  const handleEnrichEvents = async (eventsToEnrich: Slide[]) => {
    await enrichEvents(eventsToEnrich as Slide[], true, true);
  };
  const handleCreateEvent = () => {
    if (isEditableTimeline) {
      const newEvent = {
        ...BLANK_SLIDE,
        id: `NEW_EVENT_FROM_TABLE`,
        displayOrder: allSlides.length + 1
      };
      onOpenModal(newEvent, 'create');
    }
  };
  const handleDuplicateEvent = useCallback((displayOrder: string) => {
    const index = Number(displayOrder) - 1;
    onSetEventIndex(index);
    const duplicate = {
      ...allSlides[index],
      show: true,
      id: `NEW_DUPLICATE_FROM_TABLE_${index}`
    };
    onOpenModal(duplicate, 'duplicate');
  }, [allSlides, onAddEvent]);
  const handleEditEvent = useCallback((displayOrder: string, column?: string | null) => {
    const index = Number(displayOrder) - 1;
    onSetEventIndex(index);
    onOpenModal({
      ...allSlides[index]
    }, column);
  }, [allSlides, onOpenModal]);
  const handleEditTitleSlide = () => {
    onOpenTitleSlideModal();
  };
  const handleDeleteEvent = useCallback((displayOrder: string) => {
    const index = Number(displayOrder) - 1;
    onSetEventIndex(index);
    onDeleteEvent(allSlides[index]);
  }, [allSlides, onDeleteEvent]);
  const handleDeleteTimeline = () => {
    setDeleteTimelineDialogOpen(false);
    onDeleteTimeline();
  };
  const handleDownloadPptx = async () => {
    setExportingPptx(true);
    setPptxJobPollCount(0);
    try {
      // Create PPTX job
      const createResponse = await FetchWithVersionCheck(`/api/protected/timelines/${timelineId}/pptx-jobs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'x-tai-id-token': idToken || ''
        }
      });
      if (!createResponse.ok) {
        const errorData = await createResponse.json().catch(() => ({
          error: 'Failed to create PPTX job'
        }));
        throw new Error(errorData.error || 'Failed to create PPTX job');
      }
      const jobData = await createResponse.json();
      if (!jobData.id) {
        throw new Error('Job ID not found in response');
      }

      // Normalize status to uppercase for comparison
      const status = String(jobData.status).toUpperCase();

      // Handle based on job status, regardless of HTTP status code
      if (status === 'COMPLETED') {
        // Job completed - trust that s3OutputUrl is valid and download immediately
        try {
          await downloadPptxFile(timelineId, jobData.id, title);
          setExportingPptx(false);
          setPptxJobId(null);
          setPptxJobPollCount(0);
        } catch (downloadError) {
          console.error('Error downloading PPTX file:', downloadError);
          setExportingPptx(false);
          setPptxJobId(null);
          setPptxJobPollCount(0);
          alert(`Failed to download PPTX: ${(downloadError as Error).message}`);
        }
      } else if (status === 'PENDING' || status === 'PROCESSING') {
        // Job is pending or processing - start polling
        setPptxJobId(jobData.id);
        setPptxJobPollCount(1);
        // Don't set exportingPptx to false - keep it true while polling
      } else if (status === 'FAILED') {
        // Job failed
        setExportingPptx(false);
        setPptxJobId(null);
        setPptxJobPollCount(0);
        throw new Error(jobData.errorMessage || 'PPTX generation failed');
      } else {
        // Unknown status - start polling to be safe
        console.warn(`Unknown PPTX job status: ${jobData.status}, starting polling`);
        setPptxJobId(jobData.id);
        setPptxJobPollCount(1);
      }
    } catch (error) {
      console.error('Error creating PPTX job:', error);
      setExportingPptx(false);
      setPptxJobId(null);
      setPptxJobPollCount(0);
      alert(`Failed to generate PPTX: ${(error as Error).message}`);
    }
  };
  const downloadPptxFile = useCallback(async (timelineId: string, jobId: string, timelineTitle?: string) => {
    try {
      const requestUrl = new URL(`/api/protected/timelines/${timelineId}/pptx-jobs/${jobId}/download`, window.location.origin);
      if (timelineTitle) {
        requestUrl.searchParams.set('title', timelineTitle);
      }
      const response = await FetchWithVersionCheck(requestUrl.toString(), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'x-tai-id-token': idToken || ''
        },
        signal: AbortSignal.timeout(600000)
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          error: 'Failed to download PPTX'
        }));
        throw new Error(errorData.detail || errorData.error || 'Failed to download PPTX');
      }
      const buffer = await response.arrayBuffer();
      const blob = new Blob([buffer], {
        type: 'application/zip'
      });
      const url = URL.createObjectURL(blob);

      // Use timeline title for filename
      const sanitizedTitle = timelineTitle?.replace(/[^a-zA-Z0-9-_ ]/g, '_') || `timeline-${timelineId}`;
      const filename = `${sanitizedTitle}.zip`;
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PPTX:', error);
      throw error;
    }
  }, [accessToken, idToken]);

  // Poll for PPTX job status
  useEffect(() => {
    if (pptxJobId && pptxJobPollCount > 0 && pptxJobPollCount < 100) {
      if (pptxJobPollRef.current) {
        clearTimeout(pptxJobPollRef.current);
        pptxJobPollRef.current = null;
      }
      pptxJobPollRef.current = setTimeout(async () => {
        try {
          const statusResponse = await FetchWithVersionCheck(`/api/protected/timelines/${timelineId}/pptx-jobs/${pptxJobId}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'x-tai-id-token': idToken || ''
            }
          });
          if (!statusResponse.ok) {
            throw new Error('Failed to check job status');
          }
          const jobData = await statusResponse.json();

          // Normalize status to uppercase for comparison
          const status = String(jobData.status).toUpperCase();
          if (status === 'COMPLETED') {
            // Job completed - trust that s3OutputUrl is valid and download
            try {
              await downloadPptxFile(timelineId, pptxJobId, title);
              setExportingPptx(false);
              setPptxJobId(null);
              setPptxJobPollCount(0);
            } catch (downloadError) {
              console.error('Error downloading PPTX file:', downloadError);
              setExportingPptx(false);
              setPptxJobId(null);
              setPptxJobPollCount(0);
              alert(`Failed to download PPTX: ${(downloadError as Error).message}`);
            }
          } else if (status === 'FAILED') {
            // Job failed
            setExportingPptx(false);
            setPptxJobId(null);
            setPptxJobPollCount(0);
            alert(`PPTX generation failed: ${jobData.errorMessage || 'Unknown error'}`);
          } else if (status === 'PENDING' || status === 'PROCESSING') {
            // Still processing - continue polling
            setPptxJobPollCount(prev => prev + 1);
          } else {
            // Unknown status - stop polling and reset
            setExportingPptx(false);
            setPptxJobId(null);
            setPptxJobPollCount(0);
            alert(`PPTX generation returned unknown status: ${jobData.status}`);
          }
        } catch (error) {
          console.error('Error polling PPTX job status:', error);
          setExportingPptx(false);
          setPptxJobId(null);
          setPptxJobPollCount(0);
          alert(`Failed to check PPTX generation status: ${(error as Error).message}`);
        }
      }, 3000); // Poll every 3 seconds
    } else if (pptxJobPollCount >= 100) {
      // Timeout after 100 attempts (5 minutes)
      setExportingPptx(false);
      setPptxJobId(null);
      setPptxJobPollCount(0);
      alert('PPTX generation timed out. Please try again.');
    }

    // Cleanup on unmount or when polling stops
    return () => {
      if (pptxJobPollRef.current) {
        clearTimeout(pptxJobPollRef.current);
        pptxJobPollRef.current = null;
      }
    };
  }, [pptxJobId, pptxJobPollCount, timelineId, accessToken, idToken, downloadPptxFile, title]);
  const handleRowActions = useCallback((displayOrder: string, action: string) => {
    if (action === 'Edit Event') {
      handleEditEvent(displayOrder);
    } else if (action === 'Duplicate Event') {
      handleDuplicateEvent(displayOrder);
    } else if (action === 'Delete Event') {
      handleDeleteEvent(displayOrder);
    }
  }, [handleEditEvent, handleDuplicateEvent, handleDeleteEvent]);
  const defaultColDef = {
    sortable: true,
    filter: true,
    editable: false,
    onCellDoubleClicked: () => {}
  };
  const sortByRealDatetimeComparator = (date1: string, date2: string, node1: any, node2: any) => {
    return sortByRealDatetime(node1.data, node2.data, timezoneListByName);
  };
  const handleSelectionChanged = (params: any) => {
    const selectedEvents = params.api.getSelectedNodes().map((node: any) => node.data);
    const newCurrentEvent = selectedEvents.length > 0 ? selectedEvents[0] : null;
    setCurrentEvent(newCurrentEvent);
    setSelectedRows(selectedEvents);
  };
  const handleDeleteSelectedEvents = () => {
    updateTimeline('delete', selectedRows);
  };
  const dragHandleClass = '[&_.ag-icon-grip::before]:h-[74px] [&_.ag-icon-grip::before]:bg-[linear-gradient(180deg,rgba(255,255,255,1)0px,rgba(15,29,50,1)70px)]';
  const columnDefs = useMemo(() => {
    const columnDefsForEditing = [{
      colId: 'rowDragHandle',
      width: 10,
      rowDrag: isLegacy || sortByDateTime ? false : true,
      sortable: false,
      filter: false,
      cellClass: dragHandleClass
    }, {
      field: 'actions',
      width: '65px',
      headerName: 'Tools',
      filter: false,
      sortable: false,
      cellRenderer: Actions,
      cellRendererParams: {
        handleRowActions: handleRowActions
      }
    }];
    const columnDefs = [{
      field: 'visibility',
      width: '35px',
      headerName: '',
      sortable: false,
      cellRenderer: Visibility,
      onCellClicked: handleVisibilityClick
    }, {
      field: 'displayOrder',
      width: '95px',
      headerName: 'Order',
      rowDrag: false,
      sortable: true,
      editable: false,
      filter: false,
      cellRenderer: DisplayOrder
    }, {
      field: 'media',
      width: 120,
      headerName: 'Media',
      cellRenderer: Media,
      onCellClicked: (params: any) => handleEditEvent(params.data.displayOrder, 'media')
    }, {
      field: 'date',
      width: 116,
      headerName: 'Date',
      sortable: true,
      cellRenderer: Dates,
      comparator: sortByRealDatetimeComparator,
      singleClickEdit: true,
      onCellClicked: (params: any) => handleEditEvent(params.data.displayOrder, 'date')
    }, {
      field: 'time',
      width: 110,
      headerName: 'Time',
      sortable: false,
      cellRenderer: Times,
      onCellClicked: (params: any) => handleEditEvent(params.data.displayOrder, 'time')
    }, {
      field: 'slideSubTitle',
      width: 100,
      headerName: 'Header',
      wrapText: true,
      sortable: true,
      cellRenderer: Descriptions,
      onCellClicked: (params: any) => handleEditEvent(params.data.displayOrder, 'slideSubTitle')
    }, {
      field: 'slideTitle',
      width: 250,
      headerName: 'Title',
      wrapText: true,
      sortable: true,
      cellRenderer: Titles,
      onCellClicked: (params: any) => handleEditEvent(params.data.displayOrder, 'slideTitle')
    }, {
      field: 'slideDescription',
      minWidth: 280,
      flex: 1,
      headerName: 'Description',
      wrapText: true,
      sortable: true,
      cellRenderer: Descriptions,
      onCellClicked: (params: any) => handleEditEvent(params.data.displayOrder, 'slideDescription')
    }, {
      field: 'sources',
      width: 180,
      headerName: 'Sources',
      sortable: true,
      cellRenderer: SimplifiedSources,
      onCellClicked: (params: any) => handleEditEvent(params.data.displayOrder, 'sources')
    }, {
      field: 'updatedAt',
      width: 180,
      headerName: 'Date Updated',
      cellRenderer: DateAdded
    }];
    return isLegacy || !isEditableTimeline ? columnDefs : [...columnDefsForEditing, ...columnDefs];
  }, [handleEditEvent, handleRowActions, handleVisibilityClick, isLegacy, isEditableTimeline, featureFlags, dragHandleClass]);
  const handleCloseModal = () => {
    onSetEventIndex(0);
    setEditingItems(null);
  };
  const getRowId = useCallback(function (params: any) {
    return String(params.data.displayOrder);
  }, []);
  const handleRowDragEnd = (params: any) => {
    const newRows: Slide[] = [];
    params.api.forEachNode((node: any) => {
      newRows.push(node.data);
    });
    updateTimeline('order', newRows);
  };
  const handleApplyOrder = () => {
    const newRows: Slide[] = [];
    gridRef.current?.api.forEachNodeAfterFilterAndSort((node: any) => {
      newRows.push(node.data);
    });
    updateTimeline('order', newRows);
  };
  const checkDisplayOrder = useCallback(() => {
    if (!gridRef.current?.api) return;

    // Get the sort state of all columns
    const columnState = gridRef.current.api.getColumnState();

    // Find if any column is sorted
    const sortedColumn = columnState.find(col => col.sort);

    // Only show Apply Order button if:
    // 1. A column other than displayOrder is sorted, or
    // 2. displayOrder column is sorted but not ascending
    setApplyOrderEnabled(!!sortedColumn && (sortedColumn.colId !== 'displayOrder' || sortedColumn.sort !== 'asc'));
  }, []);
  const handleSortChanged = (params: any) => {
    checkDisplayOrder();

    // Get current sort state and update dropdown/button
    const sortModel = params.api.getColumnState().find((col: any) => col.sort);
    if (sortModel) {
      // Find the column definition to get the header name
      const colDef = columnDefs.find(col => col.field === sortModel.colId);
      setSelectedSortColumn(colDef?.headerName || colDef?.field || null);
      setSortDirection(sortModel.sort as 'asc' | 'desc');
    } else {
      // No sorting applied - clear both states
      setSortDirection('none');
      setSelectedSortColumn(null);
    }
  };
  const handleSortColumnSelect = (column: string) => {
    setSelectedSortColumn(column);
    setSortDirection('asc'); // Set to ascending by default when column is selected
    if (gridRef.current?.api) {
      const colDef = columnDefs.find(col => col.headerName === column || col.field === column);
      if (colDef) {
        // Apply ascending sort when column is selected
        gridRef.current.api.applyColumnState({
          state: [{
            colId: colDef.field || '',
            sort: 'asc'
          }],
          defaultState: {
            sort: null
          }
        });
        checkDisplayOrder();
      }
    }
  };
  const handleSortDirectionToggle = () => {
    // Cycle through: asc -> desc -> none -> asc
    const newDirection = sortDirection === 'asc' ? 'desc' : sortDirection === 'desc' ? 'none' : 'asc';
    setSortDirection(newDirection);
    if (gridRef.current?.api && selectedSortColumn) {
      const colDef = columnDefs.find(col => col.headerName === selectedSortColumn || col.field === selectedSortColumn);
      if (colDef) {
        if (newDirection === 'none') {
          // Clear sorting
          gridRef.current.api.applyColumnState({
            defaultState: {
              sort: null
            }
          });
        } else {
          // Apply new sort direction
          gridRef.current.api.applyColumnState({
            state: [{
              colId: colDef.field || '',
              sort: newDirection
            }],
            defaultState: {
              sort: null
            }
          });
        }
        checkDisplayOrder();
      }
    }
  };
  const getTitleSlideTitle = () => {
    if (!title) return 'ADD TITLE SLIDE';
    return title.length > 25 ? `${title.substring(0, 25)}...` : title;
  };
  return <div data-testid="timelineTableView" className={`flex flex-col h-full w-full p-0`} data-sentry-component="TPTable" data-sentry-source-file="tp-table.tsx">
      <HotKeys onCloseModal={handleCloseModal} isModal={!!editingItems} data-sentry-element="HotKeys" data-sentry-source-file="tp-table.tsx" />
      <Modal isOpen={deleteTimelineDialogOpen} onOpenChange={setDeleteTimelineDialogOpen} isDismissable onClose={() => setDeleteTimelineDialogOpen(false)} data-sentry-element="Modal" data-sentry-source-file="tp-table.tsx">
        <ModalContent className='bg-gray-900 border-2 border-gray-200 rounded-lg text-white' data-sentry-element="ModalContent" data-sentry-source-file="tp-table.tsx">
          <ModalHeader className='bg-gray-800' data-sentry-element="ModalHeader" data-sentry-source-file="tp-table.tsx">
            Confirm Deletion of this Timeline
          </ModalHeader>
          <ModalBody className='bg-gray-700' data-sentry-element="ModalBody" data-sentry-source-file="tp-table.tsx">
            <p>Are you sure you want to delete this Timeline? This action cannot be undone.</p>
          </ModalBody>
          <ModalFooter className='bg-gray-800' data-sentry-element="ModalFooter" data-sentry-source-file="tp-table.tsx">
            <Button className='bg-red-300 hover:bg-red-300 border-red-600 h-[30px]' onPress={handleDeleteTimeline} data-sentry-element="Button" data-sentry-source-file="tp-table.tsx">Delete</Button>
            <Button className='bg-gray-300 hover:bg-gray-300 border-gray-600 h-[30px]' onPress={() => setDeleteTimelineDialogOpen(false)} data-sentry-element="Button" data-sentry-source-file="tp-table.tsx">Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal isOpen={deleteSelectedEventsDialogOpen} onOpenChange={setDeleteSelectedEventsDialogOpen} isDismissable onClose={() => setDeleteSelectedEventsDialogOpen(false)} data-sentry-element="Modal" data-sentry-source-file="tp-table.tsx">
        <ModalContent className='bg-gray-900 border-2 border-gray-200 rounded-lg text-white' data-sentry-element="ModalContent" data-sentry-source-file="tp-table.tsx">
          <ModalHeader className='bg-gray-800' data-sentry-element="ModalHeader" data-sentry-source-file="tp-table.tsx">
            Confirm Deletion of Selected Events
          </ModalHeader>
          <ModalBody className='bg-gray-700' data-sentry-element="ModalBody" data-sentry-source-file="tp-table.tsx">
            <p>Are you sure you want to delete these events? This action cannot be undone.</p>
          </ModalBody>
          <ModalFooter className='bg-gray-800' data-sentry-element="ModalFooter" data-sentry-source-file="tp-table.tsx">
            <Button className='bg-red-300 hover:bg-red-300 border-red-600 h-[30px]' onPress={handleDeleteSelectedEvents} data-sentry-element="Button" data-sentry-source-file="tp-table.tsx">Delete</Button>
            <Button className='bg-gray-300 hover:bg-gray-300 border-gray-600 h-[30px]' onPress={() => setDeleteSelectedEventsDialogOpen(false)} data-sentry-element="Button" data-sentry-source-file="tp-table.tsx">Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal isOpen={enrichSelectedEventsDialogOpen} onOpenChange={setEnrichSelectedEventsDialogOpen} isDismissable onClose={() => setEnrichSelectedEventsDialogOpen(false)} data-sentry-element="Modal" data-sentry-source-file="tp-table.tsx">
        <ModalContent className='bg-gray-900 border-2 border-gray-200 rounded-lg text-white' data-sentry-element="ModalContent" data-sentry-source-file="tp-table.tsx">
          <ModalHeader className='bg-gray-800' data-sentry-element="ModalHeader" data-sentry-source-file="tp-table.tsx">
            Confirm Enrichment of Selected Events
          </ModalHeader>
          <ModalBody className='bg-gray-700' data-sentry-element="ModalBody" data-sentry-source-file="tp-table.tsx">
            <p>Enriching events may change the Description, and Sources/Media. Are you sure you want to enrich all selected events?</p>
          </ModalBody>
          <ModalFooter className='bg-gray-800' data-sentry-element="ModalFooter" data-sentry-source-file="tp-table.tsx">
            <Button className='bg-red-300 hover:bg-red-300 border-red-600 h-[30px]' onPress={() => handleEnrichEvents(selectedRows)} data-sentry-element="Button" data-sentry-source-file="tp-table.tsx">Enrich</Button>
            <Button className='bg-gray-300 hover:bg-gray-300 border-gray-600 h-[30px]' onPress={() => setEnrichSelectedEventsDialogOpen(false)} data-sentry-element="Button" data-sentry-source-file="tp-table.tsx">Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <div className='flex'>
        <main className='flex flex-col'>
          <div className='ag-theme-quartz-dark w-[100vw] h-[100vh] sm:w-[calc(100vw-60px)] sm:h-[calc(100vh-125px)]'>
            {!allSlides && <div className='flex flex-col h-[calc(100vh-62px)] items-center justify-center overflow-hidden'>
                <h1 className="text-4xl text-white">Enter Document ID above to view the timeline in table view.</h1> 
              </div>}
            {!!allSlides && <div className={`ag-theme-quartz-dark w-[100vw] sm:w-[calc(100vw-60px)] ${featureFlags?.workProductNav ? (featureFlags?.enableMediaAndSourceShare ? 'sm:h-[calc(100vh-299px)]' : 'sm:h-[calc(100vh-260px)]') + ' h-[calc(100vh-150px)]' : 'sm:h-[calc(100vh-180px)] h-[calc(100vh-100px)]'}`}>
                {isEditableTimeline && <div className="box-border hidden sm:flex flex-col sm:flex-row gap-4 sm:items-end sm:justify-between h-auto mt-[-10px] mb-[40px] sm:mb-[15px] w-full">
                    <div className="flex flex-col sm:flex-row sm:items-end gap-2">
                      {isEditableTimeline && <div className="flex flex-row items-center sm:items-start gap-2 sm:border-1 border-white/5 border-solid rounded-lg p-1">
                          <Tooltip className="bg-gray-800 border-2 border-gray-200 rounded-lg text-white p-2 w-[200px] text-center" content="Edit timeline title slide." placement="bottom" delay={0}>
                            <button data-testid="EditTitleSlideButton" aria-label="Edit title slide" className="sm:btn-text sm:py-2.5" onClick={event => {
                      handleEditTitleSlide();
                      recordStat('EditTitleSlideButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
                    }}>
                              <NotePencilIcon weight="duotone" className="w-[20px] h-[20px] sm:w-[25px] sm:h-[25px]" />
                            </button>
                          </Tooltip>
                          <div className="flex flex-row sm:flex-col gap-1 m-0 p-0">
                            <label className="hidden sm:flex text-white/65 font-base text-[14px] uppercase tracking-wider">
                              Timeline: 
                            </label>
                            <h2 className="text-white font-light text-[16px] sm:text-[24px] leading-none m-0 p-0">{getTitleSlideTitle()}</h2>
                          </div>
                        </div>}
                      <div className="flex flex-row gap-2 items-center">
                        {selectedRows.length > 0 && <Dropdown classNames={{
                    base: 'bg-gray-800 rounded-lg border-1 border-white/10 border-solid text-white shadow shadow-inner p-0 m-0 w-[142.8px]',
                    content: 'flex bg-transparent items-start shadow-none rounded-none m-0 p-0 min-w-fit'
                  }} onOpenChange={open => {
                    if (!open) return;
                  }}>
                            <DropdownTrigger>
                              <button aria-label="Edit selected events" aria-haspopup="menu" className={`btn-text flex flex-row gap-2 items-center justify-between py-[7.5px] min-w-[142.8px] ${selectedSortColumn ? 'rounded-r-none border-r-0' : ''}`}>
                                <span>Edit Selected Events</span>
                                <CaretDownIcon size={12} />
                              </button>
                            </DropdownTrigger>
                            <DropdownMenu selectionMode="single" variant="flat" classNames={{
                      base: 'box-border bg-transparent w-full shadow-none pb-0',
                      list: 'text-white p-0 m-0 mb-1'
                    }}>
                              <DropdownItem key={'Enrich'} onPress={event => {
                        setEnrichSelectedEventsDialogOpen(true);
                        recordStat('EnrichSelectedEventsButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
                      }} classNames={{
                        base: 'w-full py-1 m-0 flex flex-row gap-1',
                        wrapper: ''
                      }}>
                                <div className="flex flex-row gap-1 items-center justify-start">
                                  <MagicWandIcon weight="duotone" size={20} />
                                  <div>Enrich</div>
                                </div>
                              </DropdownItem>
                              <DropdownItem key={'Delete'} onPress={event => {
                        setDeleteSelectedEventsDialogOpen(true);
                        recordStat('DeleteSelectedEventsButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
                      }} classNames={{
                        base: 'w-full py-1 m-0 text-red-700 hover:text-red-600',
                        wrapper: ''
                      }}>
                                <div className="flex flex-row gap-1 items-center justify-start text-red-700 hover:text-red-900">
                                  <TrashIcon weight="duotone" size={20} />
                                  <div>Delete</div>
                                </div>
                              </DropdownItem>
                            </DropdownMenu>
                          </Dropdown>}
                        <button data-testid="AddEventButton" aria-label="Add new event" className="btn-text gap-2" tabIndex={0} onClick={event => {
                    handleCreateEvent();
                    recordStat('AddEventButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
                  }}>
                          <ListPlusIcon weight="duotone" size={20} />
                          <span>Add Event</span>
                        </button>
                        {isEditableTimeline && <>
                            <Dropdown classNames={{
                      base: 'bg-gray-800 rounded-lg border-1 border-white/10 border-solid text-white shadow shadow-inner p-0 m-0 w-[142.8px]',
                      content: 'flex bg-transparent items-start shadow-none rounded-none m-0 p-0 min-w-fit'
                    }} onOpenChange={open => {
                      if (!open) return;
                    }}>
                              <DropdownTrigger>
                                <button aria-label="Sort by column" aria-haspopup="menu" className={`btn-text flex flex-row gap-2 items-center justify-between py-[7.5px] min-w-[142.8px] ${selectedSortColumn ? 'rounded-r-none border-r-0' : ''}`}>
                                  <span>{selectedSortColumn || 'Sort by Column'}</span>
                                  <CaretDownIcon size={12} />
                                </button>
                              </DropdownTrigger>
                              <DropdownMenu selectionMode="single" variant="flat" classNames={{
                        base: 'box-border bg-transparent w-full shadow-none pb-0',
                        list: 'text-white p-0 m-0 mb-1'
                      }}>
                                {columnDefs.filter(col => 'sortable' in col && col.sortable !== false).map(col => <DropdownItem key={col.field || ''} onPress={() => handleSortColumnSelect(col.headerName || col.field || '')} classNames={{
                          base: 'w-full py-1 m-0',
                          wrapper: ''
                        }}>
                                      {col.headerName || col.field}
                                    </DropdownItem>)}
                              </DropdownMenu>
                            </Dropdown>
                            {selectedSortColumn && <button aria-label={sortDirection === 'desc' ? "Sort ascending" : "Sort descending"} className="btn-text flex flex-row gap-2 ml-[-10px] pl-[7px] pr-[6px] rounded-l-none" onClick={handleSortDirectionToggle}>
                                {sortDirection === 'desc' ? <SortAscendingIcon size={20} /> : <SortDescendingIcon size={20} />}
                              </button>}
                            {applyOrderEnabled && <Tooltip className="bg-gray-800 border-2 border-gray-200 rounded-lg text-white p-2 w-[200px] text-center" content="Use current sorting for presentation event order." placement="bottom" delay={0}>
                                <div className="flex flex-row items-center h-full">
                                  <button data-testid="ApplyOrderButton" className="btn-text gap-2 bg-green-700 hover:bg-green-600 p-[8px]" onClick={event => {
                          handleApplyOrder();
                          recordStat('ApplyOrderButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
                        }}>
                                    <span>Save Order</span>
                                  </button>
                                </div>
                              </Tooltip>}
                          </>}
                        {isEditableTimeline && eventsReviewModalExtractionJob && <EventsReviewModal isOpen={eventsReviewModalOpen} onClose={() => handleCloseEventsReviewModal(false)} question={eventsReviewModalQuestion} extractionJob={eventsReviewModalExtractionJob} setApplyCardOpen={() => handleCloseEventsReviewModal(true)} createEventsFromEntityEvents={createEventsFromEntityEvents} getEventsFromEntityService={getEventsFromEntityService} />}
                        {isEditableTimeline && <Popover classNames={{
                    base: 'bg-gray-800 rounded-lg border-1 border-white/10 border-solid text-white w-[300px]',
                    content: 'flex bg-transparent items-start shadow-none rounded-none m-0 p-2'
                  }} placement="bottom-start" onOpenChange={open => {
                    setExtractedEventsPopoverOpen(open);
                  }}>
                            <PopoverTrigger>
                              <button type="button" aria-label="Extracted Events" aria-haspopup="dialog" className="btn-text flex flex-row gap-2 items-center justify-between py-[7.5px] min-w-[142.8px]" onClick={event => {
                        event.stopPropagation();
                        event.preventDefault();
                        if (!loadingExtractedEvents && !extractedEventsPopoverOpen) {
                          setLoadingExtractedEvents(true);
                          getEventsFromEntityService('GET_ALL_JOBS', '', caseId, '', [], 0, null, true);
                        }
                      }}>
                                <span>Extracted Events</span>
                                <CaretDownIcon size={12} />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="bg-gray-700 box-border flex flex-col gap-2 items-center justify-between sm:w-[500px] w-[95vw]">
                              {!loadingExtractedEvents && extractionJobs && extractionJobs.length > 0 ? <>
                                  {extractionJobs.map(job => <div key={job.jobId} className="bg-gray-800 text-white/80 hover:bg-gray-900 hover:text-white/100 box-border rounded-sm flex flex-row gap-2 items-center justify-between w-full">
                                      <button className="relative box-border p-2 rounded-sm w-full cursor-pointer disabled:cursor-not-allowed" onClick={() => {
                            setEventsReviewModalQuestion(job.question);
                            setEventsReviewModalExtractionJob(job);
                            setEventsReviewModalOpen(true);
                          }} disabled={job.status === 'pending' || jobsBeingApplied[job.jobId] || jobsBeingDeleted[job.jobId]}>
                                        {job.question}
                                        {job.status === 'pending' || jobsBeingApplied[job.jobId] || jobsBeingDeleted[job.jobId] ? <Spinner size="sm" color="white" variant="wave" className="absolute right-1 top-0 bg-gray-800 rounded-sm" /> : <CheckIcon size={20} className="text-green-700 absolute right-1 top-0 bg-gray-800 rounded-sm" />}
                                      </button>
                                    </div>)}
                                </> : <>
                                  {loadingExtractedEvents ? <div className="bg-gray-800 text-white/80 hover:bg-gray-900 hover:text-white/100 box-border rounded-sm flex flex-row gap-1 items-center justify-center w-full p-2">
                                      <div className="flex flex-row items-center justify-center">
                                        Checking for Extracted Events
                                      </div>
                                      <div className="flex flex-row items-center justify-center pb-2">
                                        <Spinner size="sm" color="white" variant="wave" className="opacity-70" />
                                      </div>
                                    </div> : <div className="bg-gray-800 text-white/80 hover:bg-gray-900 hover:text-white/100 box-border rounded-sm flex flex-row gap-2 items-center justify-center w-full p-2">
                                      No Extracted Events
                                    </div>}
                                </>}
                            </PopoverContent>
                          </Popover>}
                      </div>
                    </div>
                    {isEditableTimeline && <div className="hidden sm:flex flex-row items-end justify-end h-full w-[391px] gap-4">
                        {featureFlags?.exportTimelinePptx && <button id="ExportTimelinePptxButton" aria-label={exportingPptx ? "Exporting timeline" : "Export timeline as PowerPoint"} className="btn-text gap-2 disabled:opacity-60 disabled:cursor-not-allowed" tabIndex={0} disabled={exportingPptx} onClick={event => {
                  handleDownloadPptx();
                  recordStat('ExportTimelinePptxButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
                }}>
                          {exportingPptx ? <div className="flex flex-row items-center gap-2">
                              <Spinner size="sm" color="white" variant="wave" />
                              <span>Exporting...</span>
                            </div> : <>
                              <DownloadSimpleIcon size={20} />
                              <span>Export PPTX</span>
                              </>}
                          </button>}
                        <button data-testid="DeleteTimelineButton" aria-label="Delete timeline" className="btn-text gap-2" tabIndex={0} onClick={event => {
                  setDeleteTimelineDialogOpen(true);
                  recordStat('DeleteTimelineButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
                }}>
                          <TrashIcon weight="duotone" size={20} />
                          <span>Delete Timeline</span>
                        </button>
                      </div>}
                  </div>}
                <div role="region" aria-label="Timeline events table" className="w-full h-full">
                  <AgGridReact ref={gridRef} onRowDragEnd={handleRowDragEnd} rowDragManaged={true} rowData={allSlides as any} getRowId={getRowId} defaultColDef={defaultColDef} columnDefs={columnDefs as any} rowDragMultiRow={true} rowSelection={isLegacy || !isEditableTimeline ? undefined : {
                mode: 'multiRow',
                checkboxes: true,
                headerCheckbox: true
              }} onSortChanged={handleSortChanged} onSelectionChanged={handleSelectionChanged} onGridReady={checkDisplayOrder} onFilterChanged={checkDisplayOrder} gridOptions={{
                rowDragManaged: true,
                suppressScrollOnNewData: true,
                getRowHeight: (params: {
                  data?: {
                    sources: any[];
                  };
                }) => {
                  const baseHeight = 90;
                  const height = baseHeight;
                  return height;
                },
                rowStyle: {
                  cursor: 'pointer'
                }
              }} theme={myTheme} />
                </div>
              </div>}
          </div>
        </main>        
      </div>
    </div>;
};
export default TPTable;